import { useState } from 'react';

interface ShareButtonsProps {
  productName: string;
  productId: string;
  price: number;
}

const DISCOUNT_CODE = 'DEEL2';

const CodePopup = ({ onClose }: { onClose: () => void }) => {
  const [codeCopied, setCodeCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm bg-gradient-to-b from-[#0d0020] to-[#050010] border border-violet-500/30 rounded-3xl p-8 text-center animate-[fadeInUp_0.3s_ease]">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Confetti icon */}
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600/30 to-green-600/20 border border-emerald-500/30 mx-auto mb-5">
          <i className="ri-gift-2-line text-emerald-400 text-3xl" />
        </div>

        <h3 className="text-white font-black text-xl mb-2">
          Jouw €2 korting!
        </h3>
        <p className="text-white/50 text-sm leading-relaxed mb-6">
          Bedankt voor het delen! Gebruik deze code bij je volgende bestelling en krijg <strong className="text-white">€2 korting</strong>.
        </p>

        {/* Code box */}
        <div className="relative mb-4">
          <div className="flex items-center justify-between gap-3 px-5 py-4 bg-[#1a0030] border-2 border-dashed border-violet-500/50 rounded-2xl">
            <div>
              <p className="text-white/35 text-xs mb-1">Kortingscode</p>
              <p className="text-white font-black text-2xl tracking-widest">{DISCOUNT_CODE}</p>
            </div>
            <button
              onClick={copyCode}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                codeCopied
                  ? 'bg-emerald-600/30 border border-emerald-500/40 text-emerald-300'
                  : 'bg-violet-600/30 border border-violet-500/40 text-violet-300 hover:bg-violet-600/50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${codeCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-sm`} />
              </div>
              {codeCopied ? 'Gekopieerd!' : 'Kopieer'}
            </button>
          </div>
        </div>

        {/* How to use */}
        <div className="flex items-start gap-3 p-4 bg-violet-900/20 border border-violet-500/15 rounded-xl text-left mb-6">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-violet-400 text-sm" />
          </div>
          <p className="text-white/45 text-xs leading-relaxed">
            Voer de code in bij het afrekenen. Geldig op alle producten, eenmalig te gebruiken per bestelling.
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
        >
          Ga verder winkelen
        </button>
      </div>
    </div>
  );
};

export const ShareButtons = ({ productName, productId, price }: ShareButtonsProps) => {
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [instaCopied, setInstaCopied] = useState(false);

  const url = `https://www.zpiritualz.com/shop/${productId}`;

  const whatsappText = `✨ Kijk eens naar dit product bij ZpiritualZ!\n\n*${productName}*\n💶 €${price.toFixed(2)}\n\n🔗 ${url}\n\n#zpiritualz #spirituelebescherming #bozeoog`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const handleWhatsappShare = () => {
    window.open(whatsappUrl, '_blank');
    // Toon popup na korte delay zodat WhatsApp eerst opent
    setTimeout(() => setShowCodePopup(true), 800);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shareInstagram = () => {
    navigator.clipboard.writeText(
      `✨ ${productName} — €${price.toFixed(2)}\n\nBekijk op zpiritualz.com/shop/${productId}\n\n#zpiritualz #spirituelebescherming #bozeoog #energiereiniging #aura #hindoestaans`
    );
    setInstaCopied(true);
    setTimeout(() => setInstaCopied(false), 2500);
  };

  return (
    <>
      {showCodePopup && <CodePopup onClose={() => setShowCodePopup(false)} />}

      <div className="space-y-3">
        {/* Promo banner — WhatsApp deel actie */}
        <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-emerald-900/25 via-green-900/15 to-emerald-900/25">
          {/* Decoratieve achtergrond */}
          <div className="absolute right-0 top-0 bottom-0 w-24 flex items-center justify-center opacity-5 pointer-events-none">
            <i className="ri-whatsapp-line text-white" style={{ fontSize: '80px' }} />
          </div>

          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-600/25 border border-emerald-500/30 shrink-0">
                <i className="ri-gift-2-line text-emerald-400 text-lg" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">
                  Deel &amp; krijg <span className="text-emerald-400">€2 korting</span>
                </p>
                <p className="text-white/45 text-xs mt-0.5 leading-relaxed">
                  Stuur dit product via WhatsApp en ontvang direct kortingscode <strong className="text-white/70">DEEL2</strong>
                </p>
              </div>
            </div>
            <button
              onClick={handleWhatsappShare}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-whatsapp-line text-sm" />
              </div>
              Deel via WhatsApp
            </button>
          </div>
        </div>

        {/* Gewone share knoppen */}
        <div className="flex items-center gap-2">
          <span className="text-white/25 text-xs font-medium whitespace-nowrap">Ook delen via:</span>
          <div className="flex items-center gap-2">
            {/* Instagram */}
            <button
              onClick={shareInstagram}
              title="Kopieer Instagram caption"
              className="group flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-pink-600/15 to-fuchsia-600/10 border border-pink-500/20 hover:border-pink-500/45 hover:bg-pink-600/20 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${instaCopied ? 'ri-check-line' : 'ri-instagram-line'} text-sm ${instaCopied ? 'text-pink-300' : 'text-pink-400'}`} />
              </div>
              <span className={`text-xs font-semibold hidden sm:block ${instaCopied ? 'text-pink-300' : 'text-pink-400'}`}>
                {instaCopied ? 'Caption gekopieerd!' : 'Instagram'}
              </span>
            </button>

            {/* Kopieer link */}
            <button
              onClick={copyLink}
              title="Kopieer productlink"
              className="group flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-violet-600/15 to-fuchsia-600/10 border border-violet-500/20 hover:border-violet-500/45 hover:bg-violet-600/20 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-sm ${copied ? 'text-violet-300' : 'text-violet-400'}`} />
              </div>
              <span className={`text-xs font-semibold hidden sm:block ${copied ? 'text-violet-300' : 'text-violet-400'}`}>
                {copied ? 'Gekopieerd!' : 'Kopieer link'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
